import * as d3 from 'd3';
import { interpolateSinebow } from 'd3-scale-chromatic';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';
import { CwcDataSet } from '../../js/generated/cwc_data.pb.js';

type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

interface PointData {
  date: Date;
  value: number;
}
interface LineSeries {
  key: string;
  name: string;
  color: string;
  points: PointData[];
  startIndex: number;
}
interface ConsoleWarsUIState extends UIState {
  currentDate: string | null;
}

interface ConsoleWarsMetaData {
  timeline: string[];
  consoles: { [key: string]: ConsoleInfo };
  seriesOrder: string[];
}
interface ConsoleInfo {
  name: string;
  releaseDate?: string;
}

export class ConsoleSalesTimeline extends BaseChart<CwcDataSet, LineSeries, ConsoleWarsUIState> {
  static override defaultConfig = {
    defaultVisibleKeys: ['FC', 'SFC', 'N64', 'GC', 'Wii', 'WiiU', 'NS', 'NS2'],
    animationDurationPerSeries: 3000, // 全体の再生時間として扱う
    lineWidth: 2.5,
    gridLineWidth: 1,
    highlightOpacity: 1.0,
    dimmedOpacity: 0.15,
    yAxisTicks: 5,
    yAxisTickPadding: 8,
    axisLabelOffsetY: 20,
    tooltipOffsetX: -100,
    tooltipOffsetY: -100,
  };

  private lineData: LineSeries[] = [];
  private seriesOrder: string[] = [];
  private timeline: Date[] = [];
  private timelineStrings: string[] = [];
  private visibleSeriesKeys: Set<string>;

  private animationStartFrame: number = 0;

  protected override fullDataSet: CwcDataSet | null = null;
  private mappingData: ConsoleWarsMetaData | null = null;

  private gridGroup: D3Selection<SVGGElement> | null = null;
  private linesGroup: D3Selection<SVGGElement> | null = null;
  private markersGroup: D3Selection<SVGGElement> | null = null; 
  
  private xScale: d3.ScaleTime<number, number> | null = null;
  private yScale: d3.ScaleLinear<number, number> | null = null;
  private colorScale: d3.ScaleOrdinal<string, string> | null = null;
  private lineGenerator: d3.Line<PointData> | null = null;
  private tooltip: D3Selection<HTMLDivElement> | null = null;
  private hoverGridLinesGroup: d3.Selection<SVGGElement, unknown, null, undefined> | null = null;
  private highlightedKey: string | null = null;

  private lineLengths: Map<string, number> = new Map();
  
  // ★追加: パス要素のキャッシュ用
  private cachedPaths: D3Selection<SVGPathElement> | null = null;

  constructor(chartElement: HTMLElement, config = {}) {
    super(chartElement, config);
    this.visibleSeriesKeys = new Set(this.config.defaultVisibleKeys);
    this.margin = { top: 20, right: 20, bottom: 50, left: 70 };
  }

  public async loadDataForUI(): Promise<UiDataForInitializer | null> {
    if (this.mappingData) {
      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    }
    const mappingUrl = (this.chartElement as HTMLElement).dataset.mappingJsonSourceUrl;
    const protoUrl = (this.chartElement as HTMLElement).dataset.protobufSourceUrl;

    if (!mappingUrl || !protoUrl) {
      this._displayMessage('データソースのURLが指定されていません。', 'error');
      return null;
    }

    try {
      const [protoResponse, mappingResponse] = await Promise.all([
        fetch(protoUrl),
        fetch(mappingUrl)
      ]);
      if (!protoResponse.ok) throw new Error(`Protobufの読み込みに失敗: ${protoResponse.statusText}`);
      if (!mappingResponse.ok) throw new Error(`マッピングJSONの読み込みに失敗: ${mappingResponse.statusText}`);

      const [protoBuffer, mappingJson] = await Promise.all([
        protoResponse.arrayBuffer(),
        mappingResponse.json()
      ]);

      const decodedMessage = CwcDataSet.decode(new Uint8Array(protoBuffer));
      this.fullDataSet = CwcDataSet.toObject(decodedMessage, { defaults: true }) as CwcDataSet;
      this.mappingData = mappingJson.meta as ConsoleWarsMetaData;

      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    } catch (error) {
      console.error('ConsoleWarsChart CRITICAL ERROR:', error);
      this._displayMessage('データファイルの読み込みに失敗しました。', 'error');
      return null;
    }
  }

  public override async init(): Promise<void> {
    if (!this.fullDataSet) {
      await this.loadDataForUI();
    }
    await super.init();
  }

  public setVisibleSeries(visibleKeys: Set<string>): void {
    this.visibleSeriesKeys = visibleKeys;
    this.resetAndRedraw();
  }

  protected override setupDimensions(): void {
    super.setupDimensions();
    this.width = Math.floor(this.width);
    this.height = Math.floor(this.height);
  }

  protected override _processData(): void {
    if (!this.fullDataSet || !this.mappingData) return;
    const mappingData = this.mappingData;
    const fullDataSet = this.fullDataSet;

    this.seriesOrder = mappingData.seriesOrder;

    const parseTime = d3.timeParse('%Y-%m');
    this.timeline = mappingData.timeline
      .map(d => parseTime(d))
      .filter((d): d is Date => d !== null);

    this.colorScale = d3.scaleOrdinal<string, string>()
      .domain(this.seriesOrder)
      .range(this.seriesOrder.map((_, i) => interpolateSinebow(i / (this.seriesOrder.length - 1))));

    this.lineData = fullDataSet.series.reduce<LineSeries[]>((acc, seriesProto) => {
      const id = seriesProto.id;
      const values = seriesProto.values;
      if (id == null || !Array.isArray(values) || id < 0 || id >= this.seriesOrder.length) {
        return acc;
      }
      const key = this.seriesOrder[id];
      const points: PointData[] = values.map((value, i) => {
        const startIndex = seriesProto.startIndex ?? 0;
        const date = this.timeline[startIndex + i];
        return { date, value };
      });
      acc.push({
        key: key,
        name: mappingData.consoles[key]?.name || key,
        color: this.colorScale!(key),
        points,
        startIndex: seriesProto.startIndex ?? 0
      });
      return acc;
    }, []);

    const formatTime = d3.timeFormat('%Y-%m');
    this.timelineStrings = this.timeline.map(formatTime);
  }

  // ★変更: BaseChartのループを使わず、初期状態を設定して待機する
  protected override resetAndRedraw(): void {
    // 既存のアニメーションを停止
    this.pause();

    this.activeLineData = this.seriesOrder
      .filter(key => this.visibleSeriesKeys.has(key))
      .map(key => this.lineData.find(d => d.key === key))
      .filter((d): d is LineSeries => !!d);

    if (this.activeLineData.length > 0) {
      this.animationStartFrame = d3.min(this.activeLineData, d => d.startIndex) ?? 0;
      const animationEndFrame = d3.max(this.activeLineData, d => d.startIndex + d.points.length - 1) ?? this.timeline.length - 1;
      this.totalFrames = animationEndFrame - this.animationStartFrame + 1;
    } else {
      this.animationStartFrame = 0;
      this.totalFrames = 0;
    }
    
    // フレームを0にリセット
    this.currentFrame = 0;

    this._initialDrawAxes();

    if (this.activeLineData.length === 0) {
      this.linesGroup?.selectAll('*').remove();
      this.markersGroup?.selectAll('*').remove();
      this.updateUI();
      return;
    }

    this.isReady = true;
    
    // 初期フレームを描画
    this.drawFrame(0);
    this.updateUI();

    if (this.config.autoplay) {
      setTimeout(() => this.play(), this.config.autoplayDelay);
    }
  }

  // ★追加: マスター補間器による再生
  public override play(): void {
    if (this.isPlaying || !this.isReady || !this.g) return;
    
    // 終了状態なら最初に戻す
    if (this.currentFrame >= this.totalFrames - 1) {
      this.currentFrame = 0;
    }

    this.isPlaying = true;
    this.updateUI(); // 再生状態へ更新

    // 残り時間 = (全体の長さ - 現在位置) / 全体の長さ * 指定時間
    const remainingRatio = 1 - (this.currentFrame / (this.totalFrames - 1 || 1));
    const duration = this.config.animationDurationPerSeries * remainingRatio;

    // 現在のフレームから最後のフレームまでの線形補間を作成
    const interpolator = d3.interpolate(this.currentFrame, this.totalFrames - 1);

    // D3 Transitionを開始
    this.g.transition()
      .duration(duration)
      .ease(d3.easeLinear) // 等速
      .tween("frameTween", () => {
        return (t: number) => {
          // tは0.0(開始)〜1.0(終了)
          this.currentFrame = interpolator(t);
          this.drawFrame(this.currentFrame);
          
          // UI更新は少し間引いても良いが、一旦毎回呼ぶ（負荷が高ければ調整）
          // RequestAnimationFrameと異なり、これはD3の内部タイマーで呼ばれる
          this.updateUI();
        };
      })
      .on("end", () => {
        this.isPlaying = false;
        this.currentFrame = this.totalFrames - 1;
        this.drawFrame(this.currentFrame); // 最終状態を確実に描画
        this.updateUI();
      });
  }

  // ★追加: トランジションの停止
  public override pause(): void {
    if (!this.g) return;
    this.isPlaying = false;
    this.g.interrupt(); // 実行中のすべてのトランジションを停止
    this.updateUI();
  }

  // ★追加: シーク処理
  public override seek(frame: number): void {
    if (!this.isReady) return;
    
    // アニメーションを停止
    this.pause();
    
    // フレーム位置を更新
    this.currentFrame = Math.max(0, Math.min(frame, this.totalFrames - 1));
    this.drawFrame(this.currentFrame);
    this.updateUI();
  }

  protected override setupLayers(): void {
    d3.select(this.chartElement).selectAll('*').remove();
    
    const totalWidth = this.width + this.margin.left + this.margin.right;
    const totalHeight = this.height + this.margin.top + this.margin.bottom;
    
    this.svg = d3.select(this.chartElement).append('svg')
      .attr('width', totalWidth).attr('height', totalHeight)
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
    
    this.g = this.svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    
    this.gridGroup = this.g.append('g').attr('class', 'grid-group');
    this.linesGroup = this.g.append('g').attr('class', 'lines-group');
    
    const wrapper = this.chartElement.closest<HTMLElement>('.chart-container-wrapper');
    if (wrapper) {
      let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip');
      if (tooltip.empty()) {
        tooltip = d3.select(wrapper).append('div').attr('class', 'chart-tooltip');
      }
      this.tooltip = tooltip;
    }
  }

  protected override _initialDrawAxes(): void {
    if (!this.g || !this.mappingData) return;
    
    this.g.selectAll('.axis').remove();
    this.g.selectAll('.axis-label').remove();
    this.g.selectAll('.markers-group').remove();
    this.g.selectAll('.hover-grid-lines').remove();

    this.hoverGridLinesGroup = this.g.append('g').attr('class', 'hover-grid-lines');

    const allActivePoints = this.activeLineData.flatMap(series => series.points);
    const xDomain = d3.extent(allActivePoints, d => d.date) as [Date, Date];
    this.xScale = d3.scaleTime().domain(xDomain).range([0, this.width]);
    const yMax = d3.max(this.activeLineData, s => d3.max(s.points, p => p.value)) || 0;
    this.yScale = d3.scaleLinear().domain([0, yMax]).range([this.height, 0]).nice();
    
    if (this.gridGroup && this.yScale) {
        this.gridGroup.selectAll('*').remove();
        const gridTicks = this.yScale.ticks(this.config.yAxisTicks);
        this.gridGroup.selectAll('line.grid-line')
            .data(gridTicks)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', 0)
            .attr('x2', this.width)
            .attr('y1', d => this.yScale!(d))
            .attr('y2', d => this.yScale!(d))
            .style('stroke', '#e9e9e9')
            .style('stroke-width', this.config.gridLineWidth)
            .style('shape-rendering', 'crispEdges');
    }

    this.lineGenerator = d3.line<PointData>()
        .x(d => this.xScale!(d.date))
        .y(d => this.yScale!(d.value));

    this.lineLengths.clear();
    
    // キャッシュをクリア
    this.cachedPaths = null;

    if (this.linesGroup) {
      this.linesGroup.selectAll('*').remove();
      
      this.activeLineData.forEach(series => {
        const pathData = this.lineGenerator!(series.points);
        if (!pathData) return;

        const pathElement = this.linesGroup!.append('path')
          .attr('class', 'line-path')
          .attr('data-key', series.key)
          .attr('d', pathData)
          .style('fill', 'none')
          .style('stroke', series.color)
          .style('stroke-width', this.config.lineWidth)
          .style('stroke-linecap', 'round')
          .style('stroke-linejoin', 'round')
          .style('opacity', 0); // 初期状態は透明

        const length = pathElement.node()?.getTotalLength() || 0;
        this.lineLengths.set(series.key, length);

        pathElement
          .style('stroke-dasharray', length)
          .style('stroke-dashoffset', length);
      });

      // ★追加: 描画したパスをまとめて選択し、キャッシュに保存
      this.cachedPaths = this.linesGroup.selectAll<SVGPathElement, unknown>('.line-path') as unknown as D3Selection<SVGPathElement>;
    }

    const xAxis = d3.axisBottom(this.xScale).ticks(d3.timeYear.every(5));
    this.g.append('g').attr('class', 'x-axis axis').attr('transform', `translate(0, ${this.height})`).call(xAxis);
    
    const yAxis = d3.axisLeft(this.yScale)
      .ticks(this.config.yAxisTicks)
      .tickFormat(d => `${(d as number) / 1_000_000}M`)
      .tickPadding(this.config.yAxisTickPadding);
    this.g.append('g').attr('class', 'y-axis axis').call(yAxis);
    
    this.markersGroup = this.g.append('g').attr('class', 'markers-group');
    this.g.append('text').attr('class', 'axis-label').attr('transform', 'rotate(-90)').attr('y', 0 - this.margin.left).attr('x', 0 - this.height / 2).attr('dy', '1em').text('普及台数');
  }

  // ★変更: DOM探索を廃止し、キャッシュとStroke-Dashoffset計算に特化
  protected override async drawFrame(relativeFrame: number): Promise<void> {
    const absoluteFrame = this.animationStartFrame + relativeFrame;
    
    if (this.cachedPaths) {
      // アロー関数内で `this` を参照するため、ローカル変数にキャプチャ
      const lineLengths = this.lineLengths;
      const activeData = this.activeLineData;

      // キャッシュされたSelectionに対して一括操作
      this.cachedPaths.style('stroke-dashoffset', function() {
         // ここでの `this` は個々の SVGPathElement
         const key = this.getAttribute('data-key');
         if (!key) return 0; // fallback

         // data-key から series 情報を引く（Mapを使っても良いが、配列findでも十分高速）
         // 最適化: activeDataは外部スコープから参照
         const series = activeData.find(s => s.key === key);
         const totalLength = lineLengths.get(key) || 0;

         if (!series) return totalLength;

         let progress = 0;
         if (absoluteFrame <= series.startIndex) {
             progress = 0;
         } else if (absoluteFrame >= series.startIndex + series.points.length - 1) {
             progress = 1;
         } else {
             const currentSeriesFrame = absoluteFrame - series.startIndex;
             const totalSeriesFrames = series.points.length - 1;
             progress = currentSeriesFrame / totalSeriesFrames;
         }
         
         // stroke-dashoffsetを計算: 
         // progress 0 -> offset = totalLength (見えない)
         // progress 1 -> offset = 0 (全部見える)
         return totalLength * (1 - progress);
      })
      .style('opacity', function() {
          const key = this.getAttribute('data-key');
          const series = activeData.find(s => s.key === key);
          if (!series) return 0;
          
          // まだ始まっていない系列は隠す
          if (absoluteFrame <= series.startIndex) return 0;
          return 1; // 始まっていれば表示 (ハイライト制御は別途)
      });
      
      // ハイライト処理の不透明度適用（必要な場合）
      // 基本的には上のstyleチェーンに統合できるが、ハイライトロジックを入れるならここ
      this._applyHighlightStyles();
    }

    // マーカーの更新は負荷軽減のため、整数フレームに丸めたときのみ、または一定間隔で行う
    // 今回はシンプルに毎回呼ぶが、重ければ間引く
    this._updateMarkers(absoluteFrame);
  }

  private _applyHighlightStyles(): void {
    if (!this.cachedPaths) return;
    const isHighlighting = this.highlightedKey !== null;
    const highlightedKey = this.highlightedKey;
    const config = this.config;

    this.cachedPaths
      .style('stroke-width', function() {
        const key = this.getAttribute('data-key');
        return key === highlightedKey ? config.lineWidth * 1.5 : config.lineWidth;
      })
      .style('opacity', function() {
         // 先ほどのopacity計算と競合しないよう注意が必要
         // ここでは「表示されているパス」に対する不透明度調整を行う
         const currentOpacity = parseFloat(d3.select(this).style('opacity'));
         if (currentOpacity === 0) return 0; // まだ始まっていないものは0のまま

         if (!isHighlighting) return config.highlightOpacity;
         const key = this.getAttribute('data-key');
         return key === highlightedKey ? config.highlightOpacity : config.dimmedOpacity;
      });
  }

  // ★変更なし: マーカーロジックは維持
  private _updateMarkers(absoluteFrame: number): void {
    if (!this.markersGroup || !this.xScale || !this.yScale || !this.mappingData) return;
    const animationEndFrame = this.animationStartFrame + this.totalFrames - 1;
    
    // 最後まで再生されたらマーカーを表示
    if (absoluteFrame < animationEndFrame - 1) { // 浮動小数点誤差考慮で少し手前
      this.markersGroup.selectAll('*').remove();
      return;
    }
    
    // 既に描画済みなら何もしない（ちらつき防止）
    if (!this.markersGroup.selectAll('.endpoint-marker').empty()) {
        return; 
    }

    this.markersGroup.selectAll('*').remove();
    this.activeLineData.forEach(series => {
      const lastPoint = series.points[series.points.length - 1];
      if (lastPoint) {
        const cx = this.xScale!(lastPoint.date);
        const cy = this.yScale!(lastPoint.value);
        const elapsedMonths = series.points.length - 1;
        
        this.markersGroup!.append('circle')
          .attr('class', 'endpoint-marker')
          .attr('cx', cx).attr('cy', cy).attr('r', 5).style('fill', series.color)
          .attr('data-key', series.key).attr('data-name', series.name)
          .attr('data-value', lastPoint.value).attr('data-months', elapsedMonths)
          .on('mouseover', (event: MouseEvent) => {
            if (this.isPlaying) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);
            target.classed('is-hovered', true).raise();
            this.highlightedKey = target.attr('data-key');
            
            // ハイライト適用
            this._applyHighlightStyles();
            
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', cx).attr('y2', this.height);
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', 0).attr('y2', cy);
            this.tooltip?.classed('is-visible', true);
          })
          .on('mousemove', (event: MouseEvent) => {
            if (this.isPlaying || !this.tooltip) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);
            const key = target.attr('data-key');
            if (!key) return;

            const consoleData = this.mappingData?.consoles[key];
            const name = consoleData?.name || key.toUpperCase();
            const releaseDate = consoleData?.releaseDate;
            const value = parseFloat(target.attr('data-value')).toLocaleString();
            const months = target.attr('data-months');

            let tooltipHtml = `<div class="tooltip-name">${name}</div>`;
            if (releaseDate) {
              tooltipHtml += `<div class="tooltip-release-date">発売日: ${releaseDate}</div>`;
            }
            tooltipHtml += `<div class="tooltip-value">${value} <span class="unit">units</span></div>`;
            tooltipHtml += `<div class="tooltip-months">${months} <span class="unit">months</span></div>`;

            this.tooltip.html(tooltipHtml);

            const wrapper = this.chartElement.closest('.chart-container-wrapper');
            if (wrapper) {
              const [mouseX, mouseY] = d3.pointer(event, wrapper);
              this.tooltip.style('left', `${mouseX + this.config.tooltipOffsetX}px`)
                .style('top', `${mouseY + this.config.tooltipOffsetY}px`);
            }
          })
          .on('mouseout', (event: MouseEvent) => {
            if (this.isPlaying) return;
            d3.select(event.currentTarget as SVGCircleElement).classed('is-hovered', false);
            this.highlightedKey = null;
            
            // ハイライト解除
            this._applyHighlightStyles();
            
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.tooltip?.classed('is-visible', false);
          });
      }
    });
  }

  protected override updateUI(): void {
    if (typeof this.onUpdateUICallback === 'function') {
      const absoluteFrame = Math.floor(this.animationStartFrame + this.currentFrame);
      const state: ConsoleWarsUIState = {
        isPlaying: this.isPlaying,
        currentFrame: Math.floor(this.currentFrame),
        totalFrames: Math.floor(this.totalFrames),
        currentDate: this.timelineStrings[absoluteFrame] ?? null
      };
      this.onUpdateUICallback(state);
    }
  }
}